import { getPartnerApp } from "../utils/partnerApp";

export type JsonRecord = Record<string, unknown>;
export class HealthcareApiError extends Error {
  constructor(message: string, public status: number, public code?: string) {
    super(message);
    this.name = "HealthcareApiError";
  }
}
export function isRecord(value: unknown): value is JsonRecord {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
export function applicationKey(): "hospital" | "lab" | "insurance" {
  const app = getPartnerApp();
  return app === "laboratory" ? "lab" : app;
}
// Application tokens are intentionally kept in memory and bound to a Clerk session.
let currentOwner: () => string | null = () => null;
let cached: { owner: string; token: string } | null = null;
export function bindSessionOwner(getOwner: () => string | null) {
  currentOwner = getOwner;
  cached = null;
}
export function clearBackendSession() { cached = null; }
export function rememberBackendSession(owner: string, token: string) {
  if (currentOwner() !== owner || !token.trim()) throw new Error("Your account changed. Please try again.");
  cached = { owner, token };
}
export function backendToken(): string | null {
  return cached?.owner === currentOwner() ? cached.token : null;
}
export async function healthcareRequest(
  endpoint: string,
  options: {
    token?: string;
    method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
    body?: object | FormData;
    signal?: AbortSignal;
  } = {}
): Promise<unknown> {
  if (!endpoint.startsWith("/api/") || endpoint.includes("..")) throw new Error("Invalid API endpoint.");
  const configured = import.meta.env.VITE_API_BASE_URL?.trim();
  if (!configured) throw new Error("The backend URL is not configured.");
  const base = new URL(configured);
  const local = import.meta.env.DEV && base.protocol === "http:" && ["localhost", "127.0.0.1"].includes(base.hostname);
  if (base.protocol !== "https:" && !local) throw new Error("The backend URL is invalid.");
  const owner = currentOwner();
  const token = options.token ?? backendToken();
  if (!owner || !token?.trim()) throw new Error("Please sign in before continuing.");
  const controller = new AbortController();
  const abort = () => controller.abort();
  options.signal?.addEventListener("abort", abort, { once: true });
  if (options.signal?.aborted) abort();
  const timer = window.setTimeout(abort, 60000);
  const multipart = options.body instanceof FormData;
  try {
    const response = await fetch(base.toString().replace(/\/+$/, "") + endpoint, {
      method: options.method ?? "GET",
      credentials: "include",
      cache: "no-store",
      signal: controller.signal,
      headers: {
        Accept: "application/json",
        Authorization: "Bearer " + token,
        "X-Application-Key": applicationKey(),
        ...(options.body && !multipart ? { "Content-Type": "application/json" } : {}),
      },
      ...(options.body ? { body: multipart ? options.body as FormData : JSON.stringify(options.body) } : {}),
    });
    const result: unknown = response.status === 204 ? null : await response.json().catch(() => null);
    if (currentOwner() !== owner) throw new Error("Your account changed. Please try again.");
    if (!response.ok) {
      const code = isRecord(result) && typeof result.code === "string" ? result.code : undefined;
      if (response.status === 401 && !options.token) {
        clearBackendSession();
        window.dispatchEvent(new Event("gluviacare:session-expired"));
      }
      throw new HealthcareApiError(
        response.status === 401 ? "Your application session expired. Please reconnect your account."
          : response.status === 403 ? "This account does not have access to this application."
          : response.status === 400 || response.status === 422 ? "Please check the required information and try again."
          : response.status === 409 ? "This record already exists or has changed. Refresh before trying again."
          : "We could not complete the request. Please try again.", response.status, code
      );
    }
    if (response.status === 204) return null;
    if (!isRecord(result) || result.success === false) throw new Error("The backend returned an invalid response.");
    return result;
  } catch (error) {
    if (controller.signal.aborted) throw new Error(options.signal?.aborted ? "The request was cancelled." : "The request timed out. Please try again.");
    if (error instanceof TypeError) throw new Error("We could not reach the backend. Please check your connection and try again.");
    throw error;
  } finally {
    window.clearTimeout(timer);
    options.signal?.removeEventListener("abort", abort);
  }
}
